import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * collection-grid — CONFIG-BY-CONFIG port of Shrine's "Collection product grid" (47-setting,
 * settings-only section). The product grid is rendered with the shared {@link ProductCard} over
 * `resolveCollectionProducts(context).products`, plus a client-side filter UI (drawer / horizontal /
 * vertical) and a sort dropdown — Shrine's backend faceting is the V1 deferral (we filter/sort the
 * rendered demo list client-side; backend `Search & Discovery` facets land with the storefront data
 * layer). Every NON-Shopify-platform Shrine setting below is wired into the Component render. Shopify-only
 * settings are still surfaced as inspector controls (so the seller sees parity) but documented as deferred
 * where they require backend faceting / app metafields / CDN liquid assets — see the `info` strings.
 *
 * className prefix in the Component: `aqx-cg-`. Colors come from theme tokens only (hex only in a color
 * default). `templates: ['collection']` — this section lives on the collection page.
 */
export const collectionGridSchema = {
    type: 'collection-grid',
    name: 'Collection product grid',
    version: 1,
    templates: ['collection'],
    fields: [
        // ── Layout ────────────────────────────────────────────────────────────────
        {
            id: 'productsPerPage',
            type: 'range',
            label: 'Products per page',
            group: 'Layout',
            default: 16,
            min: 8,
            max: 48,
            step: 4,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Columns on desktop',
            group: 'Layout',
            default: 4,
            min: 1,
            max: 5,
            step: 1,
        },
        {
            id: 'stretchCards',
            type: 'checkbox',
            label: 'Stretch cards to fill the row',
            group: 'Layout',
            default: false,
        },
        // « Aligner les produits à gauche, au centre ou à droite » (22/09/2026): where the cards sit when a
        // row is not full — a one-product collection used to leave its card glued to the left edge.
        {
            id: 'cardsAlignment',
            type: 'select',
            label: 'Products alignment',
            group: 'Layout',
            default: 'start',
            info: 'Where the cards sit when a row is not full.',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        // ── Mobile layout ─────────────────────────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Columns on mobile',
            group: 'Mobile layout',
            default: '2',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        // ── Product card ──────────────────────────────────────────────────────────
        {
            id: 'collectionHiddenTag',
            type: 'text',
            label: 'Hidden-product tag',
            group: 'Product card',
            info: 'Shopify-only (tag-based faceting): products carrying this tag are hidden from the grid. Backend faceting deferred — shown for parity.',
            default: 'hidden',
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Product card',
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'square', label: 'Square (1:1)' },
            ],
        },
        {
            id: 'showSecondaryImage',
            type: 'checkbox',
            label: 'Show second image on hover',
            group: 'Product card',
            default: false,
        },
        {
            id: 'badges',
            type: 'select',
            label: 'Badges',
            group: 'Product card',
            default: 'regular',
            options: [
                { value: 'disabled', label: 'Disabled' },
                { value: 'regular', label: 'Regular (sale / sold out)' },
                { value: 'custom', label: 'Custom (from product tags)' },
            ],
        },
        {
            id: 'showVendor',
            type: 'checkbox',
            label: 'Show vendor',
            group: 'Product card',
            default: false,
        },
        {
            id: 'showRating',
            type: 'checkbox',
            label: 'Show product rating',
            group: 'Product card',
            info: 'Rating source is the reviews app/metafield (Shopify-only); the card draws demo stars until reviews wiring lands.',
            default: false,
        },
        {
            id: 'enableQuickAdd',
            type: 'checkbox',
            label: 'Enable quick add',
            group: 'Product card',
            info: 'The quick-add control itself is portable; Shrine ships a CDN quick-add.css/js asset (Shopify-only) — we render an in-card button instead.',
            default: false,
        },
        {
            id: 'buttonFuncionality',
            type: 'select',
            label: 'Quick-add behaviour',
            group: 'Product card',
            default: 'add-to-cart',
            options: [
                { value: 'add-to-cart', label: 'Add to cart' },
                { value: 'link-to-product-page', label: 'Link to product page' },
            ],
            visibleWhen: { field: 'enableQuickAdd', equals: true },
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: 'Product card',
            default: 'Voir le produit',
            defaultKey: 'collection.go_to_product',
            visibleWhen: { field: 'buttonFuncionality', equals: 'link-to-product-page' },
        },
        // ── Color swatches ────────────────────────────────────────────────────────
        {
            id: 'swatchesOptionName',
            type: 'text',
            label: 'Swatch option name',
            group: 'Color swatches',
            info: 'The variant option (e.g. "Color") rendered as on-card swatches. Depends on the variant-option model + theme swatch registry.',
            default: 'Color',
        },
        {
            id: 'swatchesPosition',
            type: 'select',
            label: 'Swatches position',
            group: 'Color swatches',
            default: 'top',
            options: [
                { value: 'top', label: 'Above title' },
                { value: 'bottom', label: 'Below price' },
            ],
        },
        {
            id: 'swatchesDesktopSize',
            type: 'range',
            label: 'Swatch size (desktop)',
            group: 'Color swatches',
            default: 36,
            min: 20,
            max: 60,
            step: 2,
            unit: 'px',
        },
        {
            id: 'swatchesMobileSize',
            type: 'range',
            label: 'Swatch size (mobile)',
            group: 'Color swatches',
            default: 24,
            min: 14,
            max: 40,
            step: 2,
            unit: 'px',
        },
        // ── Filtering ─────────────────────────────────────────────────────────────
        {
            id: 'enableFiltering',
            type: 'checkbox',
            label: 'Enable filtering',
            group: 'Filtering',
            info: 'Backend faceting (Shopify Search & Discovery). V1 filters the rendered list client-side over availability / price / option facets.',
            default: true,
        },
        {
            id: 'filterType',
            type: 'select',
            label: 'Filter layout',
            group: 'Filtering',
            default: 'horizontal',
            options: [
                { value: 'horizontal', label: 'Horizontal bar' },
                { value: 'vertical', label: 'Vertical sidebar' },
                { value: 'drawer', label: 'Drawer' },
            ],
            visibleWhen: { field: 'enableFiltering', equals: true },
        },
        {
            id: 'facetsVerticalOpenRows',
            type: 'select',
            label: 'Vertical: open facet groups',
            group: 'Filtering',
            default: 'first',
            options: [
                { value: 'none', label: 'None' },
                { value: 'first', label: 'First only' },
                { value: 'all', label: 'All' },
            ],
            visibleWhen: { field: 'filterType', equals: 'vertical' },
        },
        // ── Filtering · Swatches ──────────────────────────────────────────────────
        {
            id: 'facetsSwatchesOptions',
            type: 'text',
            label: 'Swatch filter names',
            group: 'Filtering · Swatches',
            info: 'Comma-separated facet names rendered as color swatches (e.g. "Color"). Category-metafield faceting + theme predefined colors are Shopify-only — the names drive client-side swatch facets in V1.',
            default: 'Color',
        },
        {
            id: 'facetsSwatchesRadius',
            type: 'range',
            label: 'Swatch corner radius',
            group: 'Filtering · Swatches',
            default: 100,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
        },
        // ── Filtering · Pills ─────────────────────────────────────────────────────
        {
            id: 'facetsPillsOptions',
            type: 'text',
            label: 'Pill filter names',
            group: 'Filtering · Pills',
            info: 'Comma-separated facet names rendered as pills (e.g. "Size, Type"). Backend faceting (Shopify-only); names drive client-side pill facets in V1.',
            default: 'Size',
        },
        {
            id: 'facetsPillsColor',
            type: 'select',
            label: 'Pill color',
            group: 'Filtering · Pills',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
            visibleWhen: { field: 'facetsPillsOptions', notEquals: '' },
        },
        {
            id: 'facetsPillsInactiveBgOpacity',
            type: 'range',
            label: 'Pill inactive bg opacity',
            group: 'Filtering · Pills',
            default: 4,
            min: 2,
            max: 25,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'facetsPillsOptions', notEquals: '' },
        },
        {
            id: 'facetsPillsInactiveBorderWidth',
            type: 'range',
            label: 'Pill inactive border width',
            group: 'Filtering · Pills',
            default: 1,
            min: 0,
            max: 4,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'facetsPillsOptions', notEquals: '' },
        },
        {
            id: 'facetsPillsInactiveBorderOpacity',
            type: 'range',
            label: 'Pill inactive border opacity',
            group: 'Filtering · Pills',
            default: 30,
            min: 5,
            max: 100,
            step: 5,
            unit: '%',
            visibleWhen: { field: 'facetsPillsOptions', notEquals: '' },
        },
        {
            id: 'facetsPillsRadius',
            type: 'range',
            label: 'Pill corner radius',
            group: 'Filtering · Pills',
            default: 100,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            visibleWhen: { field: 'facetsPillsOptions', notEquals: '' },
        },
        // ── Filtering · Price range ───────────────────────────────────────────────
        {
            id: 'facetsPriceColor',
            type: 'select',
            label: 'Price slider color',
            group: 'Filtering · Price range',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'text', label: 'Text' },
            ],
        },
        {
            id: 'facetsPricePricePosition',
            type: 'select',
            label: 'Price labels position',
            group: 'Filtering · Price range',
            default: 'top',
            options: [
                { value: 'top', label: 'Above slider' },
                { value: 'bottom', label: 'Below slider' },
            ],
        },
        {
            id: 'facetsPriceBtnStyle',
            type: 'select',
            label: 'Price apply button style',
            group: 'Filtering · Price range',
            default: 'outline',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'outline', label: 'Outline' },
            ],
        },
        {
            id: 'facetsPriceBtnBorderWidth',
            type: 'range',
            label: 'Price button border width',
            group: 'Filtering · Price range',
            default: 3,
            min: 1,
            max: 8,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'facetsPriceBtnStyle', equals: 'outline' },
        },
        // ── Filtering · Active filters ────────────────────────────────────────────
        {
            id: 'activeButtonsText',
            type: 'text',
            label: 'Active filter label',
            group: 'Filtering · Active filters',
            info: 'Tokens [filter] and [value] expand to the active facet name / value.',
            default: '[value]',
        },
        {
            id: 'activeButtonsTextColor',
            type: 'select',
            label: 'Active filter text color',
            group: 'Filtering · Active filters',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
            ],
        },
        {
            id: 'activeButtonsBgType',
            type: 'select',
            label: 'Active filter background',
            group: 'Filtering · Active filters',
            default: 'overlay',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid' },
                { value: 'overlay', label: 'Overlay' },
            ],
        },
        {
            id: 'activeButtonsBgColor',
            type: 'select',
            label: 'Active filter bg color',
            group: 'Filtering · Active filters',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
            ],
            visibleWhen: { field: 'activeButtonsBgType', notEquals: 'none' },
        },
        {
            id: 'activeButtonsBgOpacity',
            type: 'range',
            label: 'Active filter bg opacity',
            group: 'Filtering · Active filters',
            default: 4,
            min: 2,
            max: 25,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'activeButtonsBgType', equals: 'overlay' },
        },
        {
            id: 'activeButtonsBorderWidth',
            type: 'range',
            label: 'Active filter border width',
            group: 'Filtering · Active filters',
            default: 1,
            min: 0,
            max: 3,
            step: 1,
            unit: 'px',
        },
        {
            id: 'activeButtonsBorderColor',
            type: 'select',
            label: 'Active filter border color',
            group: 'Filtering · Active filters',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
            ],
            visibleWhen: { field: 'activeButtonsBorderWidth', notEquals: 0 },
        },
        {
            id: 'activeButtonsBorderOpacity',
            type: 'range',
            label: 'Active filter border opacity',
            group: 'Filtering · Active filters',
            default: 40,
            min: 10,
            max: 100,
            step: 5,
            unit: '%',
            visibleWhen: { field: 'activeButtonsBorderWidth', notEquals: 0 },
        },
        // ── Filtering · Drawer ────────────────────────────────────────────────────
        {
            id: 'facetsDrawerOpenMode',
            type: 'select',
            label: 'Open filter style',
            group: 'Filtering · Drawer',
            default: 'accordion',
            options: [
                { value: 'accordion', label: 'Collapse' },
                { value: 'slide', label: 'Slide' },
            ],
        },
        {
            id: 'facetsDrawerOpenRows',
            type: 'select',
            label: 'Drawer: open facet groups',
            group: 'Filtering · Drawer',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'first', label: 'First only' },
                { value: 'all', label: 'All' },
            ],
            visibleWhen: { field: 'facetsDrawerOpenMode', equals: 'accordion' },
        },
        {
            id: 'facetsDrawerSeparatorOpacity',
            type: 'range',
            label: 'Drawer separator opacity',
            group: 'Filtering · Drawer',
            default: 5,
            min: 0,
            max: 20,
            step: 1,
            unit: '%',
        },
        {
            id: 'facetsDrawerStickyButton',
            type: 'select',
            label: 'Drawer sticky apply button',
            group: 'Filtering · Drawer',
            default: 'after_scrolling',
            options: [
                { value: 'dont', label: "Don't stick" },
                { value: 'after_scrolling', label: 'After scrolling' },
                { value: 'always', label: 'Always' },
            ],
        },
        {
            id: 'facetsDrawerStickyButtonColor',
            type: 'select',
            label: 'Sticky button color',
            group: 'Filtering · Drawer',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'facetsDrawerStickyButton', notEquals: 'dont' },
        },
        {
            id: 'facetsDrawerStickyButtonTextColor',
            type: 'color',
            label: 'Sticky button text color',
            group: 'Filtering · Drawer',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'facetsDrawerStickyButtonColor', equals: 'custom' },
        },
        {
            id: 'facetsDrawerStickyButtonBgColor',
            type: 'color',
            label: 'Sticky button bg color',
            group: 'Filtering · Drawer',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'facetsDrawerStickyButtonColor', equals: 'custom' },
        },
        {
            id: 'facetsDrawerStickyButtonRadius',
            type: 'range',
            label: 'Sticky button corner radius',
            group: 'Filtering · Drawer',
            default: 100,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            visibleWhen: { field: 'facetsDrawerStickyButton', notEquals: 'dont' },
        },
        // ── Sorting ───────────────────────────────────────────────────────────────
        {
            id: 'enableSorting',
            type: 'checkbox',
            label: 'Enable sorting',
            group: 'Sorting',
            info: 'Shopify drives sort options from collection.sort_options; V1 sorts the rendered list client-side (featured / price / alpha / date).',
            default: true,
        },
        // ── Section (universal: scheme + padding + width) ─────────────────────────
        // Shrine's padding_top / padding_bottom (0–100 step 4) are covered by the universal padding fields.
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [],
};
export const collectionGridPresets = [
    {
        name: 'Collection product grid',
        settings: {
            productsPerPage: 16,
            columnsDesktop: 4,
            stretchCards: false,
            columnsMobile: '2',
            imageRatio: 'adapt',
            showSecondaryImage: true,
            badges: 'regular',
            showVendor: false,
            showRating: false,
            enableQuickAdd: false,
            buttonFuncionality: 'add-to-cart',
            swatchesOptionName: 'Color',
            swatchesPosition: 'top',
            swatchesDesktopSize: 36,
            swatchesMobileSize: 24,
            enableFiltering: true,
            filterType: 'horizontal',
            facetsVerticalOpenRows: 'first',
            facetsSwatchesOptions: 'Color',
            facetsSwatchesRadius: 100,
            facetsPillsOptions: 'Type',
            facetsPillsColor: 'accent-1',
            facetsPriceColor: 'accent-1',
            facetsPricePricePosition: 'top',
            facetsPriceBtnStyle: 'outline',
            activeButtonsText: '[value]',
            activeButtonsBgType: 'overlay',
            facetsDrawerOpenMode: 'accordion',
            facetsDrawerStickyButton: 'after_scrolling',
            enableSorting: true,
            width: 'wide',
        },
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const collectionGridMeta = {
    label: 'Collection product grid',
    group: 'Collection',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map